import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { env } from '../config/env';
import { cartService } from '../services/cart/cartService';
import { canAddToCart, getUnitPrice } from '../utils/product';
import { cartItemsToUpdateRequest, cartItemCount, mergeCartItems, serverCartToItems } from '../utils/cartSync';
import { clearStoredCart, loadStoredCart, saveStoredCart } from '../utils/cartStorage';
import type { CartContextType, CartItem, Product } from '../types';
import CartToast from '../components/CartToast';
import { useAuth } from './AuthContext';

const CART_TOAST_MESSAGE = 'Ürün sepete eklendi';
const CART_TOAST_DURATION_MS = 3000;
const CART_SYNC_DEBOUNCE_MS = 400;

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const [items, setItems] = useState<CartItem[]>(() => loadStoredCart());
  const [cartToastVisible, setCartToastVisible] = useState(false);
  const skipNextSyncRef = useRef(false);
  const syncTimerRef = useRef<number | null>(null);
  const cartToastTimerRef = useRef<number | null>(null);
  const itemsRef = useRef(items);
  const syncChainRef = useRef(Promise.resolve());

  itemsRef.current = items;

  const showCartToast = useCallback(() => {
    setCartToastVisible(true);

    if (cartToastTimerRef.current) {
      window.clearTimeout(cartToastTimerRef.current);
    }

    cartToastTimerRef.current = window.setTimeout(() => {
      setCartToastVisible(false);
      cartToastTimerRef.current = null;
    }, CART_TOAST_DURATION_MS);
  }, []);

  const runServerSync = useCallback(() => {
    if (env.useMock || !isAuthenticated) return;

    syncChainRef.current = syncChainRef.current
      .then(async () => {
        await cartService.updateCart(cartItemsToUpdateRequest(itemsRef.current));
      })
      .catch(() => {});
  }, [isAuthenticated]);

  const scheduleServerSync = useCallback(() => {
    if (env.useMock || !isAuthenticated) return;

    if (syncTimerRef.current) {
      window.clearTimeout(syncTimerRef.current);
    }

    syncTimerRef.current = window.setTimeout(() => {
      syncTimerRef.current = null;
      runServerSync();
    }, CART_SYNC_DEBOUNCE_MS);
  }, [isAuthenticated, runServerSync]);

  const flushServerSync = useCallback(() => {
    if (syncTimerRef.current) {
      window.clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }

    runServerSync();
  }, [runServerSync]);

  const commitItems = useCallback(
    (updater: (prev: CartItem[]) => CartItem[]) => {
      setItems((prev) => {
        const next = updater(prev);
        if (skipNextSyncRef.current) {
          skipNextSyncRef.current = false;
        } else {
          scheduleServerSync();
        }
        return next;
      });
    },
    [scheduleServerSync],
  );

  useEffect(() => {
    saveStoredCart(items);
  }, [items]);

  useEffect(() => {
    if (env.useMock || !isAuthenticated || authLoading) return;

    let cancelled = false;

    void (async () => {
      try {
        const cart = await cartService.fetchCart();
        if (cancelled) return;

        const serverItems = serverCartToItems(cart.items);

        setItems((prev) => {
          const merged = mergeCartItems(prev, serverItems);
          const needsSync = cartItemCount(merged) !== cartItemCount(serverItems);

          if (needsSync) {
            queueMicrotask(() => scheduleServerSync());
          }

          return merged;
        });
      } catch {
        // Sepet yüklenemezse yerel durum korunur
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, scheduleServerSync, user?.id]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      flushServerSync();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (syncTimerRef.current) {
        window.clearTimeout(syncTimerRef.current);
      }
      if (cartToastTimerRef.current) {
        window.clearTimeout(cartToastTimerRef.current);
      }
    };
  }, [flushServerSync]);

  const addToCart = (product: Product, quantity = 1) => {
    if (!canAddToCart(product)) return;

    let added = false;

    commitItems((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      const currentQty = existing?.quantity ?? 0;
      const available = product.stock - currentQty;

      if (available <= 0) return prev;

      const addQty = Math.min(quantity, available);
      added = true;

      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + addQty, product }
            : item,
        );
      }

      return [...prev, { product, quantity: addQty }];
    });

    if (added) {
      showCartToast();
    }
  };

  const removeFromCart = (productId: number) => {
    commitItems((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    commitItems((prev) =>
      prev.map((item) => {
        if (item.product.id !== productId) return item;
        const capped = Math.min(quantity, item.product.stock);
        return { ...item, quantity: capped };
      }),
    );
  };

  const clearCart = useCallback(
    (options?: { syncServer?: boolean }) => {
      skipNextSyncRef.current = true;
      setItems([]);
      clearStoredCart();

      const syncServer = options?.syncServer ?? true;
      if (syncServer && !env.useMock && isAuthenticated) {
        void cartService.clearCart().catch(() => {});
      }
    },
    [isAuthenticated],
  );

  const totalItems = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items],
  );

  const totalPrice = useMemo(
    () => items.reduce((sum, item) => sum + getUnitPrice(item.product) * item.quantity, 0),
    [items],
  );

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
      <CartToast message={CART_TOAST_MESSAGE} visible={cartToastVisible} />
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
